import { useMutation, useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CollapseChevron } from "components/CollapseChevron";
import { Dialog } from "components/Dialog";
import { FilePreview } from "components/Document/shared/FilePreview";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { gql } from "generated-graphql";
import {
  BulkManifestDiscrepanciesQuery,
  DiscrepancyKind,
  DocumentType,
  WasteManifestSource,
} from "generated-graphql/graphql";
import { useCtrlWheelHorizontalScroll } from "hooks/useCtrlWheelHorizontalScroll";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { CREATE_EXCEPTION_REPORT_SUBMISSION, PROCESSED_MANIFESTS_QUERY } from "./api";
import { CompliancePane } from "./CompliancePane";
import {
  type ComplianceItem,
  type ComplianceItemMissing,
  getDeadlineBand,
  mapEpaCorrectionDiscrepancy,
} from "./CompliancePane.helpers";
import {
  ALL_DISCREPANCY_STATUSES,
  type DiscrepancyStatus,
  formatDateOnly,
  formatDisplayValue,
  getDiscrepancyBadgeCase,
  getDiscrepancyStatus,
  getFieldLabel,
  getStatusChipIcon,
  getStatusChipTooltip,
  getStatusColor,
  getStatusLabel,
  isComplianceDiscrepancy,
  isDiscrepancyStatus,
  supportsVendorDataUpdate,
} from "./DiscrepancyReviewDialog.helpers";
import { DiscrepancySection, SECTION_TITLE_HEADER_HEIGHT } from "./DiscrepancySection";
import { DiscrepancySummaryBar } from "./DiscrepancySummaryBar";
import { FieldIssuesPane } from "./FieldIssuesPane";
import { ManifestCommentsPopover } from "./ManifestCommentsPopover";
import { SOURCE_LABEL } from "./manifestSourceDisplay";
import { MultiSelectFilter } from "./MultiSelectFilter";
import { ProofOfContactDialog } from "./ProofOfContactDialog";
import { ResolveDiscrepanciesDialog } from "./ResolveDiscrepanciesDialog";
import { useRangeSelection } from "./useRangeSelection";

function renderDiscrepancyBadges(d: {
  eManifestValue?: string | null;
  vendorValue?: string | null;
  manifestDocumentValue?: string | null;
}): React.ReactNode {
  const badgeCase = getDiscrepancyBadgeCase(d);
  if (badgeCase == null) return null;
  const chipSx = { fontSize: "0.65rem", height: 16 };
  return (
    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
      {(badgeCase === "vendor-only" || badgeCase === "both") && (
        <Chip label="vs Vendor" size="small" variant="outlined" sx={chipSx} />
      )}
      {(badgeCase === "doc-only" || badgeCase === "both") && (
        <Chip
          label={`vs ${SOURCE_LABEL[WasteManifestSource.ManifestDocument]}`}
          size="small"
          variant="outlined"
          sx={chipSx}
        />
      )}
    </Stack>
  );
}

export const BULK_MANIFEST_DISCREPANCIES_QUERY = gql(`
  query BulkManifestDiscrepancies($processedManifestIds: [ID!]!) {
    bulkManifestDiscrepancies(processedManifestIds: $processedManifestIds) {
      id
      processedManifestId
      manifestTrackingNumber
      facilityName
      epaId
      wasteLineUnifiedId
      wasteLineNumber
      field
      isDot
      kind
      severity
      status
      eManifestValue
      vendorValue
      manifestDocumentValue
      vendorDiscrepant
      documentDiscrepant
      sourceDocumentId
      sourceDocumentFileExtension
      escalation
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
      createdAt
      ruleKey
      wasteManifestCorrectionRequest {
        id
        status
        requestDate
        industryRespondedAt
        initiatorRole
        sections
      }
    }
  }
`);

const UPDATE_DISCREPANCY_VENDOR_DATA = gql(`
  mutation UpdateDiscrepancyVendorData($discrepancyIds: [ID!]!, $note: String) {
    updateDiscrepancyVendorData(discrepancyIds: $discrepancyIds, note: $note) {
      id
      processedManifestId
      field
      status
      eManifestValue
      vendorValue
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
    }
  }
`);

const RESOLVE_DISCREPANCIES_EXTERNALLY = gql(`
  mutation ResolveDiscrepanciesExternally($discrepancyIds: [ID!]!, $note: String) {
    resolveDiscrepanciesExternally(discrepancyIds: $discrepancyIds, note: $note) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
    }
  }
`);

const REOPEN_PENDING_DISCREPANCIES = gql(`
  mutation ReopenPendingDiscrepancies($discrepancyIds: [ID!]!) {
    reopenPendingDiscrepancies(discrepancyIds: $discrepancyIds) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
    }
  }
`);

const DISMISS_DISCREPANCIES = gql(`
  mutation DismissDiscrepancies($discrepancyIds: [ID!]!, $note: String) {
    dismissDiscrepancies(discrepancyIds: $discrepancyIds, note: $note) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
    }
  }
`);

export const COMPLIANCE_MANIFEST_DATA_QUERY = gql(`
  query ComplianceManifestData($search: String, $page: Int, $pageSize: Int) {
    processedManifests(search: $search, page: $page, pageSize: $pageSize) {
      items {
        id
        shippedDate
        desFacilityName
        transporterContactedAt
        transporterContactedBy
        documents {
          document {
            id
            documentType
          }
        }
        exceptionReportSubmission {
          id
          title
          status
        }
      }
    }
  }
`);

// rem-based so the sticky offsets track root font-size (≈37px at 16px root, the small-table header
// height). Sticky stack, top to bottom: section title band (top: 0, in DiscrepancySection), then
// the table's column header row, then the per-facility group rows.
const STICKY_COLUMN_HEADER_HEIGHT = "2.3125rem";
const STICKY_COLUMN_HEADER_TOP = SECTION_TITLE_HEADER_HEIGHT;
const STICKY_SECTION_HEADER_TOP = `calc(${SECTION_TITLE_HEADER_HEIGHT} + ${STICKY_COLUMN_HEADER_HEIGHT})`;

type ActionKey = "updateVendorData" | "resolveExternally" | "nonDiscrepancy";

type SkipGroup = {
  reason: string;
  count: number;
};

type ActionEligibility = {
  eligibleIds: string[];
  skips: SkipGroup[];
};

function computeActionEligibility(
  action: ActionKey,
  selected: readonly Discrepancy[]
): ActionEligibility {
  const eligibleIds: string[] = [];
  const skipCounts = new Map<string, number>();
  const addSkip = (reason: string) => {
    skipCounts.set(reason, (skipCounts.get(reason) ?? 0) + 1);
  };

  for (const d of selected) {
    if (action === "updateVendorData") {
      if (d.status === "IGNORED") {
        addSkip("already a non-discrepancy");
      } else if (d.status !== "UNRESOLVED") {
        addSkip("already resolved or pending");
      } else if (!supportsVendorDataUpdate(d.field)) {
        addSkip(`${getFieldLabel(d.field)} doesn't support vendor data update`);
      } else {
        eligibleIds.push(d.id);
      }
    } else if (action === "resolveExternally") {
      if (d.status === "IGNORED") {
        addSkip("already a non-discrepancy");
      } else if (d.status === "RESOLVED") {
        addSkip("already resolved");
      } else if (d.status === "PENDING") {
        addSkip("already pending");
      } else {
        eligibleIds.push(d.id);
      }
    } else {
      if (d.status === "IGNORED") {
        addSkip("already a non-discrepancy");
      } else if (d.status === "RESOLVED") {
        addSkip("already resolved");
      } else if (isComplianceDiscrepancy(d)) {
        addSkip("compliance row — use Resolve in e-Manifest instead");
      } else {
        eligibleIds.push(d.id);
      }
    }
  }

  const skips = [...skipCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);

  return { eligibleIds, skips };
}

function formatSkipSummary(skips: SkipGroup[]): string {
  return skips.map((s) => `• ${s.count} ${s.reason}`).join("\n");
}

/**
 * Distinguishes "this section has no items at all" from "this section has
 * items, but none match the current Status filter" — same 0 count, very
 * different explanation for why. Returns undefined for the former, letting
 * the caller fall back to its default placeholder text.
 */
function emptyMessageFor(filteredCount: number, totalCount: number): string | undefined {
  if (totalCount > 0 && filteredCount === 0) {
    return "No items match the current status filter.";
  }
  return undefined;
}

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

function SectionHeaderRow({
  label,
  count,
  colSpan,
  collapsed,
  onToggle,
}: {
  label: string;
  count: number;
  colSpan: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // MUI's `grey` palette is mode-independent — flip to darker shades in dark mode.
  const subtleBg = isDark ? theme.palette.grey[900] : theme.palette.grey[100];
  const subtleHover = isDark ? theme.palette.grey[800] : theme.palette.grey[200];
  const subtleBorder = isDark ? theme.palette.grey[800] : theme.palette.grey[200];
  return (
    <TableRow
      onClick={onToggle}
      sx={{
        cursor: "pointer",
        "&:hover": { bgcolor: subtleHover },
      }}
    >
      <TableCell
        colSpan={colSpan}
        sx={{
          bgcolor: subtleBg,
          py: 0.75,
          px: 0,
          fontWeight: 600,
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${subtleBorder}`,
          position: "sticky",
          top: STICKY_SECTION_HEADER_TOP,
          zIndex: 1,
        }}
      >
        {/* Inner sticky wrapper pins the label + chevron to the left edge during horizontal scroll. */}
        <Box
          sx={{
            position: "sticky",
            left: 0,
            width: "fit-content",
            px: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CollapseChevron collapsed={collapsed} />
            <span>{label}</span>
            <Chip size="small" label={count} sx={{ height: 20, fontSize: "0.75rem" }} />
          </Stack>
        </Box>
      </TableCell>
    </TableRow>
  );
}

/**
 * Per-action definitions shared by the resolve modal's options. Kept as data
 * so the Data Source Conflicts and Data Quality Issues sections build the
 * same modal, minus "Correct Vendor Data" for the latter — a Data Quality
 * Issue's eManifestValue is the flagged (invalid) value itself, so copying it
 * into vendor data would propagate the problem and falsely mark it resolved.
 */
const RESOLVE_ACTION_DEFS: Record<ActionKey, { label: string; description: string }> = {
  updateVendorData: {
    label: "Correct Vendor Data",
    description:
      "Overwrite vendor data with e-Manifest values. Eligible rows will be marked as resolved.",
  },
  resolveExternally: {
    label: "Resolve in e-Manifest",
    description:
      'Mark as being resolved in e-Manifest (e.g. updating the value in RCRAInfo). These will move to "Pending" until the change propagates back through the system.',
  },
  nonDiscrepancy: {
    label: "Non-Discrepancy",
    description:
      "Mark as non-discrepancies. The difference is expected and will be hidden from view.",
  },
};

function DiscrepancyBulkActionBar({
  totalSelected,
  loading,
  onOpen,
}: {
  totalSelected: number;
  loading: boolean;
  onOpen: () => void;
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1.5 }}>
      <Tooltip
        title={totalSelected === 0 ? "Select rows to resolve them in bulk" : ""}
        placement="top"
      >
        <span>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={<TaskAltIcon />}
            disabled={totalSelected === 0 || loading}
            onClick={onOpen}
          >
            Resolve Selected{totalSelected > 0 ? ` (${totalSelected})` : ""}…
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
}

export type DiscrepancySectionKey = "epaCorrection" | "eManifestMissing" | "field" | "fieldIssue";

const ALL_SECTION_KEYS: readonly DiscrepancySectionKey[] = [
  "epaCorrection",
  "eManifestMissing",
  "field",
  "fieldIssue",
];

const SECTION_LABELS: Record<DiscrepancySectionKey, string> = {
  epaCorrection: "EPA-Requested Corrections",
  eManifestMissing: "Missing e-Manifests",
  field: "Data Source Conflicts",
  fieldIssue: "Data Quality Issues",
};

interface DiscrepancyReviewDialogProps {
  processedManifestIds: string[];
  manifestTrackingNumber?: string;
  wasteLineUnifiedIds?: string[];
  /** Which section to scroll into view on open. Defaults to EPA-Requested Corrections (the primary surface). */
  initialSection?: DiscrepancySectionKey;
  /**
   * Explicit tenant for cross-tenant (staff) callers that aren't on a tenant-scoped
   * route (e.g. the staff Manifest Review queue). Defaults to the route's tenantId.
   */
  tenantId?: string;
  onClose: () => void;
}

export function DiscrepancyReviewDialog({
  processedManifestIds,
  manifestTrackingNumber,
  wasteLineUnifiedIds,
  initialSection = "epaCorrection",
  tenantId: tenantIdProp,
  onClose,
}: DiscrepancyReviewDialogProps) {
  const { tenantId: routeTenantId } = useParams<{ tenantId: string }>();
  const tenantId = tenantIdProp ?? routeTenantId;
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // Sticky surfaces need to be opaque; replicate Paper's elevation-24 overlay (16% white) in dark mode.
  const stickyOpaquePanelBg = isDark
    ? {
        backgroundColor: theme.palette.background.paper,
        backgroundImage: `linear-gradient(${alpha(
          theme.palette.common.white,
          0.16
        )}, ${alpha(theme.palette.common.white, 0.16)})`,
      }
    : { backgroundColor: theme.palette.background.paper };
  const subtleSurfaceBg = isDark ? theme.palette.grey[900] : theme.palette.grey[50];
  const rowDividerColor = isDark ? theme.palette.grey[800] : theme.palette.grey[100];
  const alerts = useAlerts();
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  const showEpaCorrectionRows = !!featureFlags[FeatureFlagKey.WasteEpaCorrectionDiscrepancies];
  // EPA-Requested Corrections doesn't exist as a concept for tenants without the
  // flag — omit it from the filterable/selectable sections entirely, not just
  // from the counts, so there's no dead "always empty" option in the list.
  const availableSectionKeys = showEpaCorrectionRows
    ? ALL_SECTION_KEYS
    : ALL_SECTION_KEYS.filter((key) => key !== "epaCorrection");
  const [statusFilter, setStatusFilter] = useState<DiscrepancyStatus[]>(() => [
    ...ALL_DISCREPANCY_STATUSES,
  ]);
  const [sectionFilter, setSectionFilter] = useState<DiscrepancySectionKey[]>(() => [
    ...availableSectionKeys,
  ]);

  // The sectionFilter initializer above only runs once, at mount — but
  // useFeatureFlags() returns a default-false snapshot until useTenant()'s
  // query resolves, so showEpaCorrectionRows can flip true on a later render.
  // Backfill "epaCorrection" into the filter the first time it becomes
  // available, so a flag that resolves after mount doesn't leave the section
  // permanently excluded. Runs once — doesn't fight a later manual uncheck.
  const epaBecameAvailableRef = useRef(false);
  useEffect(() => {
    if (!showEpaCorrectionRows || epaBecameAvailableRef.current) return;
    epaBecameAvailableRef.current = true;
    setSectionFilter((prev) =>
      prev.includes("epaCorrection") ? prev : [...prev, "epaCorrection"]
    );
  }, [showEpaCorrectionRows]);

  const tableScrollRef = useCtrlWheelHorizontalScroll();

  const epaSectionRef = useRef<HTMLDivElement>(null);
  const missingSectionRef = useRef<HTMLDivElement>(null);
  const fieldSectionRef = useRef<HTMLDivElement>(null);
  const fieldIssueSectionRef = useRef<HTMLDivElement>(null);
  const didInitialScrollRef = useRef(false);

  // Stable "now" so deadline math doesn't jitter across re-renders during the same dialog session.
  const nowRef = useMemo(() => new Date(), []);

  // Source of truth: the document the user asked to preview. Apollo's keyed useQuery discards
  // in-flight responses when the target changes, so a slow fetch can't clobber a newer selection.
  const [previewTarget, setPreviewTarget] = useState<{
    documentId: string;
    fileExtension: string;
  } | null>(null);

  const { data: previewUrlData, loading: previewUrlLoading } = useQuery(
    GET_DOCUMENT_DOWNLOAD_LINK_QUERY,
    {
      variables: { id: previewTarget?.documentId ?? "" },
      skip: !previewTarget,
      fetchPolicy: "network-only",
      onError: () => {
        // Clear the target so previewLoading doesn't hang the panel in its skeleton forever.
        alerts.error("Failed to load document preview");
        setPreviewTarget(null);
      },
    }
  );

  const previewDoc = useMemo(() => {
    if (!previewTarget) return null;
    if (previewUrlLoading) return null;
    const url = previewUrlData?.getDocumentDownloadLink;
    if (!url) return null;
    return {
      documentId: previewTarget.documentId,
      fileExtension: previewTarget.fileExtension,
      url,
    };
  }, [previewTarget, previewUrlLoading, previewUrlData]);

  const previewLoading = previewTarget != null && previewDoc == null;

  // Always the target's id (not previewDoc's) so row highlights activate synchronously on click.
  const activePreviewDocId = previewTarget?.documentId ?? null;

  const openDocumentPreview = useCallback(
    (documentId: string, fileExtension: string | null | undefined) => {
      // Functional setter keeps the same-doc no-op guard without depending on previewTarget,
      // so the callback identity stays stable across preview changes.
      setPreviewTarget((prev) =>
        prev?.documentId === documentId
          ? prev
          : { documentId, fileExtension: fileExtension ?? "pdf" }
      );
    },
    []
  );

  const openDocumentInNewTab = useCallback(() => {
    if (!previewDoc?.url) return;
    window.open(previewDoc.url, "_blank");
  }, [previewDoc]);

  // Operation name strings refetch all active instances regardless of variable differences.
  const parentRefetchQueries = ["ProcessedManifests", "ComplianceManifestData"];

  const [updateVendorData, { loading: updatingVendorData }] = useMutation(
    UPDATE_DISCREPANCY_VENDOR_DATA,
    { refetchQueries: parentRefetchQueries }
  );

  const [resolveExternally, { loading: resolvingExternally }] = useMutation(
    RESOLVE_DISCREPANCIES_EXTERNALLY,
    { refetchQueries: parentRefetchQueries }
  );

  const [dismiss, { loading: dismissing }] = useMutation(DISMISS_DISCREPANCIES, {
    // Refetch so the dismissed row updates to its Non-Discrepancy status (it
    // stays in the list — the resolver now returns IGNORED rows and the Status
    // filter controls whether they're shown).
    refetchQueries: [...parentRefetchQueries, "BulkManifestDiscrepancies"],
  });

  const [reopenPending, { loading: reopening }] = useMutation(REOPEN_PENDING_DISCREPANCIES, {
    refetchQueries: parentRefetchQueries,
  });

  const mutationLoading = updatingVendorData || resolvingExternally || dismissing || reopening;

  // The rows the resolve modal is acting on — a section's selection (bulk
  // button) or a single row (row action). includeVendorAction is false for
  // Data Quality Issues (see RESOLVE_ACTION_DEFS).
  const [resolveTarget, setResolveTarget] = useState<{
    rows: Discrepancy[];
    includeVendorAction: boolean;
    clearSelection: () => void;
  } | null>(null);

  // Per-row kebab menu. Holds the anchor element and the row it was opened for.
  const [rowMenu, setRowMenu] = useState<{
    anchorEl: HTMLElement;
    discrepancy: Discrepancy;
  } | null>(null);

  const handleReopenPending = useCallback(
    async (discrepancy: Discrepancy) => {
      setRowMenu(null);
      try {
        await reopenPending({ variables: { discrepancyIds: [discrepancy.id] } });
        alerts.success("Discrepancy marked as unresolved");
      } catch (err) {
        alerts.error(
          "Failed to mark discrepancy as unresolved",
          err instanceof Error ? err : undefined
        );
      }
    },
    [reopenPending, alerts]
  );

  const { data, loading } = useQuery(BULK_MANIFEST_DISCREPANCIES_QUERY, {
    variables: { processedManifestIds },
  });

  useEffect(() => {
    if (loading || didInitialScrollRef.current) return;
    const targetRef = {
      epaCorrection: epaSectionRef,
      eManifestMissing: missingSectionRef,
      field: fieldSectionRef,
      fieldIssue: fieldIssueSectionRef,
    }[initialSection];
    // jsdom (unit tests) doesn't implement scrollIntoView — guard with a type check
    // rather than relying on the element existing.
    if (typeof targetRef.current?.scrollIntoView === "function") {
      targetRef.current.scrollIntoView({ block: "start" });
      didInitialScrollRef.current = true;
      return;
    }
    // The ref hasn't attached yet, but the target section might not be able
    // to render at all (flag off, or filtered out) — in that case there's
    // nothing to wait for, so stop retrying instead of leaving the effect to
    // silently re-run on every render forever.
    const isTargetRenderable =
      sectionFilter.includes(initialSection) &&
      (initialSection !== "epaCorrection" || showEpaCorrectionRows);
    if (!isTargetRenderable) {
      didInitialScrollRef.current = true;
    }
  }, [loading, initialSection, sectionFilter, showEpaCorrectionRows]);

  const compliancePmIds = useMemo(() => {
    const raw = data?.bulkManifestDiscrepancies ?? [];
    const ids = new Set<string>();
    for (const d of raw) {
      if (isComplianceDiscrepancy(d)) {
        ids.add(d.processedManifestId);
      }
    }
    return [...ids];
  }, [data?.bulkManifestDiscrepancies]);

  const { data: complianceData } = useQuery(COMPLIANCE_MANIFEST_DATA_QUERY, {
    variables: {
      search: `tenantId:${tenantId} id:${compliancePmIds.join("⁋")}`,
      page: 0,
      pageSize: compliancePmIds.length,
    },
    skip: compliancePmIds.length === 0 || !tenantId,
  });

  const complianceManifestMap = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof complianceData>["processedManifests"]["items"][number]
    >();
    for (const item of complianceData?.processedManifests?.items ?? []) {
      map.set(item.id, item);
    }
    return map;
  }, [complianceData]);

  const [createExceptionReport, { loading: creatingExceptionReport }] = useMutation(
    CREATE_EXCEPTION_REPORT_SUBMISSION,
    {
      refetchQueries: [
        PROCESSED_MANIFESTS_QUERY,
        BULK_MANIFEST_DISCREPANCIES_QUERY,
        COMPLIANCE_MANIFEST_DATA_QUERY,
      ],
    }
  );

  const [showProofOfContactForPmId, setShowProofOfContactForPmId] = useState<string | null>(null);

  // Shared by EPA-Requested Corrections' and Missing e-Manifests' CompliancePane —
  // both compliance kinds resolve/report identically, only their `items` differ.
  const onResolveInEManifest = useCallback(
    async (discrepancyId: string) => {
      try {
        await resolveExternally({ variables: { discrepancyIds: [discrepancyId] } });
        alerts.success("Marked as pending e-Manifest resolution");
      } catch {
        alerts.error("Failed to mark as pending");
      }
    },
    [resolveExternally, alerts]
  );
  const onAddProofOfContact = useCallback((pmId: string) => setShowProofOfContactForPmId(pmId), []);
  const onCreateExceptionReport = useCallback(
    async (pmId: string) => {
      try {
        await createExceptionReport({ variables: { processedManifestId: pmId } });
        alerts.success("Exception report submission created");
      } catch {
        alerts.error("Failed to create exception report submission");
      }
    },
    [createExceptionReport, alerts]
  );

  const allDiscrepancies = useMemo(() => {
    // Auto-ignored rows (no resolvedBy — the pipeline dismissed a mismatch that
    // converged externally) are non-discrepancies with no review story to tell;
    // hide them entirely. Human-dismissed rows stay: they carry the reviewer's
    // "Non-Discrepancy" verdict, which the Status filter can surface.
    const raw = (data?.bulkManifestDiscrepancies ?? []).filter(
      (d) => !(d.status === "IGNORED" && !d.resolvedBy)
    );
    // Hide EPA correction rows when the flag is off, in case any were seeded during a flag-on window.
    const kindFiltered = showEpaCorrectionRows
      ? raw
      : raw.filter((d) => d.kind !== DiscrepancyKind.EpaCorrectionRequest);
    if (!wasteLineUnifiedIds) return kindFiltered;
    const wluSet = new Set(wasteLineUnifiedIds);
    return kindFiltered.filter((d) => d.wasteLineUnifiedId && wluSet.has(d.wasteLineUnifiedId));
  }, [data?.bulkManifestDiscrepancies, wasteLineUnifiedIds, showEpaCorrectionRows]);

  const epaCorrectionItems = useMemo<ComplianceItem[]>(() => {
    const items: ComplianceItem[] = [];
    for (const d of allDiscrepancies) {
      if (d.kind !== DiscrepancyKind.EpaCorrectionRequest) continue;
      const status = getDiscrepancyStatus(d);
      const epa = mapEpaCorrectionDiscrepancy({ ...d, status });
      if (epa) {
        // The pure helper doesn't see Apollo data; apply tsdfName from the compliance pm fetch.
        const pm = complianceManifestMap.get(d.processedManifestId);
        items.push({ ...epa, tsdfName: pm?.desFacilityName ?? null });
      }
    }
    return items;
  }, [allDiscrepancies, complianceManifestMap]);

  const missingEManifestItems = useMemo<ComplianceItem[]>(() => {
    const items: ComplianceItem[] = [];
    for (const d of allDiscrepancies) {
      // EPA correction rows take priority (mirrors the DB's GENERATED kind
      // column, which resolves EPA_CORRECTION_REQUEST first) — without this,
      // a row with both wasteManifestCorrectionRequestId and escalation set
      // would double-count into both sections.
      if (d.kind === DiscrepancyKind.EpaCorrectionRequest) continue;
      if (!isComplianceDiscrepancy(d)) continue;
      const status = getDiscrepancyStatus(d);
      const pm = complianceManifestMap.get(d.processedManifestId);
      const proofDoc = pm?.documents?.find(
        (link) => link.document.documentType === DocumentType.WasteTransporterContactProof
      )?.document;
      const missing: ComplianceItemMissing = {
        kind: "missing",
        id: d.id,
        processedManifestId: d.processedManifestId,
        manifestTrackingNumber: d.manifestTrackingNumber ?? null,
        epaId: d.epaId ?? null,
        generatorName: d.facilityName ?? null,
        tsdfName: pm?.desFacilityName ?? null,
        sourceDocumentId: d.sourceDocumentId ?? null,
        sourceDocumentFileExtension: d.sourceDocumentFileExtension ?? null,
        status,
        escalation: d.escalation ?? null,
        shippedDate: pm?.shippedDate ?? null,
        transporterContactedAt: pm?.transporterContactedAt ?? null,
        transporterContactedBy: pm?.transporterContactedBy ?? null,
        transporterContactProofDocumentId: proofDoc?.id ?? null,
        exceptionReportSubmissionId: pm?.exceptionReportSubmission?.id ?? null,
        exceptionReportSubmissionStatus: pm?.exceptionReportSubmission?.status ?? null,
        exceptionReportTitle: pm?.exceptionReportSubmission?.title ?? null,
      };
      items.push(missing);
    }
    return items;
  }, [allDiscrepancies, complianceManifestMap]);

  // Intentionally the raw status-filtered count, including resolved items —
  // matches how Data Source Conflicts/Data Quality Issues count (see `discrepancies`/
  // `fieldIssues` below). The old combined "Regulatory issues" tab badge
  // excluded resolved items to show "how many need attention"; the section
  // header's count now means "how many match the current Status filter" for
  // every section alike. `hasUrgent` (below) is what still flags urgency.
  const filteredEpaCorrectionItems = useMemo(
    () =>
      statusFilter.length === 0
        ? []
        : epaCorrectionItems.filter((i) => statusFilter.includes(i.status)),
    [epaCorrectionItems, statusFilter]
  );
  const filteredMissingEManifestItems = useMemo(
    () =>
      statusFilter.length === 0
        ? []
        : missingEManifestItems.filter((i) => statusFilter.includes(i.status)),
    [missingEManifestItems, statusFilter]
  );

  const epaHasUrgent = useMemo(
    () =>
      filteredEpaCorrectionItems.some(
        (i) => i.status !== "resolved" && getDeadlineBand(i, nowRef) === "crit"
      ),
    [filteredEpaCorrectionItems, nowRef]
  );
  const missingHasUrgent = useMemo(
    () =>
      filteredMissingEManifestItems.some(
        (i) => i.status !== "resolved" && getDeadlineBand(i, nowRef) === "crit"
      ),
    [filteredMissingEManifestItems, nowRef]
  );

  const isLineItemMode = Boolean(wasteLineUnifiedIds);
  const isSingleManifest = processedManifestIds.length === 1;

  let title: string;
  if (isLineItemMode) {
    title = `Line Item Discrepancies — ${wasteLineUnifiedIds?.length ?? 0} line items`;
  } else if (isSingleManifest) {
    title = `Manifest Discrepancies — #${manifestTrackingNumber ?? processedManifestIds[0].slice(0, 8)}`;
  } else {
    title = `Bulk Discrepancy Review — ${processedManifestIds.length} manifests`;
  }

  // Only kind=FIELD_DISCREPANCY rows belong in Data Source Conflicts; other
  // kinds render elsewhere. Kept status-unfiltered so a section can tell
  // "nothing here" apart from "nothing matches the current status filter"
  // (see emptyMessage below).
  const allFieldDiscrepancies = useMemo(
    () => allDiscrepancies.filter((d) => d.kind === DiscrepancyKind.FieldDiscrepancy),
    [allDiscrepancies]
  );
  const allFieldIssueDiscrepancies = useMemo(
    () => allDiscrepancies.filter((d) => d.kind === DiscrepancyKind.FieldIssue),
    [allDiscrepancies]
  );

  const discrepancies = useMemo(() => {
    if (statusFilter.length === 0) return [];
    return allFieldDiscrepancies.filter((d) => statusFilter.includes(getDiscrepancyStatus(d)));
  }, [allFieldDiscrepancies, statusFilter]);

  const fieldIssues = useMemo(() => {
    if (statusFilter.length === 0) return [];
    return allFieldIssueDiscrepancies.filter((d) => statusFilter.includes(getDiscrepancyStatus(d)));
  }, [allFieldIssueDiscrepancies, statusFilter]);

  const {
    selectedIds: fieldSelectedIds,
    toggle: toggleFieldSelect,
    clear: clearFieldSelection,
  } = useRangeSelection({ items: discrepancies });

  const {
    selectedIds: fieldIssueSelectedIds,
    toggle: toggleFieldIssueSelect,
    clear: clearFieldIssueSelection,
  } = useRangeSelection({ items: fieldIssues });

  // The resolve modal's options: the caller-appropriate action definitions
  // with eligibility computed against the targeted rows.
  const resolveOptions = useMemo(() => {
    if (!resolveTarget) return [];
    const actions: ActionKey[] = resolveTarget.includeVendorAction
      ? ["updateVendorData", "resolveExternally", "nonDiscrepancy"]
      : ["resolveExternally", "nonDiscrepancy"];
    return actions.map((action) => {
      const { eligibleIds, skips } = computeActionEligibility(action, resolveTarget.rows);
      return {
        action,
        ...RESOLVE_ACTION_DEFS[action],
        eligibleIds,
        skipSummary: skips.length > 0 ? formatSkipSummary(skips) : undefined,
      };
    });
  }, [resolveTarget]);

  const executeResolveAction = useCallback(
    async (action: ActionKey, eligibleIds: string[], note: string | null) => {
      if (!resolveTarget) return;
      const { label } = RESOLVE_ACTION_DEFS[action];
      try {
        if (action === "updateVendorData") {
          await updateVendorData({ variables: { discrepancyIds: eligibleIds, note } });
          alerts.success(`${eligibleIds.length} discrepancies resolved — vendor data updated`);
        } else if (action === "resolveExternally") {
          await resolveExternally({ variables: { discrepancyIds: eligibleIds, note } });
          alerts.success(
            `${eligibleIds.length} discrepancies marked as pending e-Manifest resolution`
          );
        } else {
          await dismiss({ variables: { discrepancyIds: eligibleIds, note } });
          alerts.success(`${eligibleIds.length} items dismissed as non-discrepancies`);
        }
        resolveTarget.clearSelection();
      } catch (err) {
        alerts.error(`Failed to ${label.toLowerCase()}`, err instanceof Error ? err : undefined);
      }
      setResolveTarget(null);
    },
    [resolveTarget, updateVendorData, resolveExternally, dismiss, alerts]
  );

  const fieldGroups = useMemo(() => {
    const groups = new Map<string, Discrepancy[]>();
    for (const d of discrepancies) {
      const existing = groups.get(d.field);
      if (existing) {
        existing.push(d);
      } else {
        groups.set(d.field, [d]);
      }
    }
    return [...groups.entries()].map(([field, items]) => ({
      field,
      label: getFieldLabel(field),
      items,
    }));
  }, [discrepancies]);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (field: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  const [collapsedSections, setCollapsedSections] = useState<Set<DiscrepancySectionKey>>(new Set());
  const toggleSectionCollapsed = (key: DiscrepancySectionKey) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // A section brought into view by a tile click shouldn't arrive collapsed.
  const expandSection = (key: DiscrepancySectionKey) => {
    setCollapsedSections((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // The summary tiles are the section-filter control (chart-legend semantics).
  // Every section showing is the neutral state — no tile lit. From neutral, a
  // click solos that section; while filtered, clicks toggle sections in and
  // out of the visible subset. Deactivating the last lit tile returns to
  // neutral, never to an empty view.
  const allSectionsShown = sectionFilter.length === availableSectionKeys.length;
  const activeTileKeys = allSectionsShown ? [] : sectionFilter;
  const handleTileClick = (key: DiscrepancySectionKey) => {
    if (allSectionsShown) {
      setSectionFilter([key]);
      expandSection(key);
      return;
    }
    if (sectionFilter.includes(key)) {
      const next = sectionFilter.filter((k) => k !== key);
      setSectionFilter(next.length === 0 ? [...availableSectionKeys] : next);
      return;
    }
    setSectionFilter([...sectionFilter, key]);
    expandSection(key);
  };

  const sectionCounts: Record<DiscrepancySectionKey, number> = {
    epaCorrection: filteredEpaCorrectionItems.length,
    eManifestMissing: filteredMissingEManifestItems.length,
    field: discrepancies.length,
    fieldIssue: fieldIssues.length,
  };
  const sectionHasUrgent: Record<DiscrepancySectionKey, boolean> = {
    epaCorrection: epaHasUrgent,
    eManifestMissing: missingHasUrgent,
    field: false,
    fieldIssue: false,
  };
  const summaryTiles = availableSectionKeys.map((key) => ({
    key,
    label: SECTION_LABELS[key],
    count: sectionCounts[key],
    hasUrgent: sectionHasUrgent[key],
  }));

  const selectedFieldDiscrepancies = useMemo(
    () => discrepancies.filter((d) => fieldSelectedIds.has(d.id)),
    [discrepancies, fieldSelectedIds]
  );
  const selectedFieldIssueDiscrepancies = useMemo(
    () => fieldIssues.filter((d) => fieldIssueSelectedIds.has(d.id)),
    [fieldIssues, fieldIssueSelectedIds]
  );

  const COL_COUNT = 8; // checkbox, facility, manifest, eManifest, vendor, document, status, row actions

  const renderValueCell = (value: string | null | undefined, discrepancy: Discrepancy) => {
    const display =
      discrepancy.field === "shippedDate" ? formatDateOnly(value) : formatDisplayValue(value);
    return (
      <TableCell
        sx={{
          maxWidth: 240,
          wordBreak: "break-word",
        }}
      >
        <Typography variant="body2" sx={{ pt: "4px" }}>
          {display}
        </Typography>
      </TableCell>
    );
  };

  const renderDiscrepancyRow = (d: Discrepancy) => {
    const status = getDiscrepancyStatus(d);
    const isSelected = fieldSelectedIds.has(d.id);
    const isPreviewedDoc =
      activePreviewDocId != null &&
      d.sourceDocumentId != null &&
      d.sourceDocumentId === activePreviewDocId;

    // Row background precedence: selected > previewed doc > default. Divider tracks the background.
    let baseBgcolor: string | undefined;
    let hoverBgcolor: string;
    let dividerColor: string;
    if (isSelected) {
      baseBgcolor = alpha(theme.palette.primary.main, 0.2);
      hoverBgcolor = alpha(theme.palette.primary.main, 0.24);
      dividerColor = alpha(theme.palette.primary.main, 0.1);
    } else if (isPreviewedDoc) {
      baseBgcolor = alpha(theme.palette.primary.main, 0.08);
      hoverBgcolor = alpha(theme.palette.primary.main, 0.12);
      dividerColor = alpha(theme.palette.primary.main, 0.1);
    } else {
      baseBgcolor = undefined;
      hoverBgcolor = theme.palette.action.hover;
      dividerColor = rowDividerColor;
    }

    return (
      <TableRow
        key={d.id}
        selected={isSelected}
        onMouseDown={(event) => {
          // Suppress native shift-click text-selection across rows; non-shift selection still works.
          if (event.shiftKey) {
            event.preventDefault();
          }
        }}
        onClick={(event) => {
          // Let interactive descendants handle their own clicks before row selection.
          if (
            event.target instanceof Element &&
            event.target.closest('button, a, input, [role="button"]')
          ) {
            return;
          }
          // A click that ends a text-selection drag is a copy gesture, not a
          // row-select intent — consuming it would both toggle the row and
          // make text feel unselectable.
          if (window.getSelection()?.toString()) {
            return;
          }
          toggleFieldSelect(d.id, { shiftKey: event.shiftKey });
        }}
        sx={{
          cursor: "pointer",
          "& .MuiTableCell-body": {
            borderBottomColor: dividerColor,
          },
          ...(baseBgcolor != null ? { bgcolor: baseBgcolor } : {}),
          // Override MUI's Mui-selected tinting so primary wins over action.selected.
          ...(isSelected
            ? {
                "&.Mui-selected": { bgcolor: baseBgcolor },
                "&.Mui-selected:hover": { bgcolor: hoverBgcolor },
              }
            : {}),
          "&:hover": {
            bgcolor: hoverBgcolor,
          },
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={(e) => {
              // shiftKey lives on the native event, not the synthetic ChangeEvent.
              const nativeEvt = e.nativeEvent;
              const shiftKey =
                nativeEvt instanceof MouseEvent || nativeEvt instanceof KeyboardEvent
                  ? nativeEvt.shiftKey
                  : false;
              toggleFieldSelect(d.id, { shiftKey });
            }}
            // Top-align: measured in-browser, 6px puts the checkbox's visual top
            // exactly on the first text line (6px cell padding + 4px text offset).
            sx={{ mt: "6px" }}
          />
        </TableCell>

        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2" sx={{ pt: "4px" }}>
            {d.facilityName ?? "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {d.epaId}
          </Typography>
        </TableCell>

        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2" fontWeight={500} sx={{ pt: "4px" }}>
            {d.manifestTrackingNumber ?? d.processedManifestId.slice(0, 8)}
          </Typography>
          {d.wasteLineUnifiedId && (
            <Typography variant="caption" color="text.secondary">
              Line {d.wasteLineNumber ?? "item"}
            </Typography>
          )}
          {renderDiscrepancyBadges(d)}
        </TableCell>

        {d.field === "wasteCodeDotConsistency" ? (
          // Within-line consistency flag: not a three-way value diff. Render the
          // synthesized "needs review" message across the eManifest + vendor
          // columns only (colSpan 2) — the document column below still renders so
          // its source-document preview eyeball stays available for verifying
          // box 9 (ENG-16015).
          <TableCell colSpan={2} sx={{ wordBreak: "break-word" }}>
            <Typography variant="body2" sx={{ pt: "4px" }}>
              {d.eManifestValue ?? "Waste code / DOT description mismatch — needs review"}
            </Typography>
          </TableCell>
        ) : (
          <>
            {renderValueCell(d.eManifestValue, d)}
            {renderValueCell(d.vendorValue, d)}
          </>
        )}

        {/* Document column — always rendered so the source-document preview
            eyeball is available for every row, including consistency flags. */}
        <TableCell sx={{ maxWidth: 240, wordBreak: "break-word" }}>
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            {d.sourceDocumentId && (
              <Tooltip title={isPreviewedDoc ? "Viewing document" : "Preview source document"}>
                <IconButton
                  size="small"
                  sx={{ pl: 0 }}
                  onClick={() => {
                    if (d.sourceDocumentId) {
                      openDocumentPreview(d.sourceDocumentId, d.sourceDocumentFileExtension);
                    }
                  }}
                >
                  {isPreviewedDoc ? (
                    <VisibilityIcon sx={{ fontSize: 16 }} color="primary" />
                  ) : (
                    <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {d.field !== "wasteCodeDotConsistency" && (
              <Typography variant="body2" sx={{ pt: "4px" }}>
                {d.field === "shippedDate"
                  ? formatDateOnly(d.manifestDocumentValue)
                  : formatDisplayValue(d.manifestDocumentValue)}
              </Typography>
            )}
          </Stack>
        </TableCell>

        <TableCell>
          {(() => {
            const tooltipText = getStatusChipTooltip(d, status);
            const statusIcon = getStatusChipIcon(d, status);
            // Use Chip's icon slot (decorative); deleteIcon would be announced as a button by AT.
            const chip = (
              <Chip
                size="small"
                label={getStatusLabel(status)}
                color={getStatusColor(status)}
                variant="outlined"
                icon={
                  statusIcon ? React.cloneElement(statusIcon, { "aria-hidden": true }) : undefined
                }
                sx={{
                  cursor: "default",
                  mt: "2px",
                  flexDirection: "row-reverse",
                  bgcolor: "background.paper",
                  "& .MuiChip-icon": {
                    fontSize: 14,
                    opacity: 0.7,
                    // row-reverse swaps left/right — pull icon-left margins to the right side.
                    marginRight: "5px",
                    marginLeft: "-2px",
                  },
                }}
              />
            );
            return tooltipText ? <Tooltip title={tooltipText}>{chip}</Tooltip> : chip;
          })()}
        </TableCell>

        <TableCell padding="checkbox">
          <Tooltip title="Row actions">
            <IconButton
              size="small"
              sx={{ mt: "6px" }}
              onClick={(event) => setRowMenu({ anchorEl: event.currentTarget, discrepancy: d })}
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth={(previewDoc ?? previewLoading) ? false : "xl"}
        fullWidth
        slotProps={{
          paper: {
            sx: {
              maxHeight: "90vh",
              height: "90vh",
              ...((previewDoc ?? previewLoading) ? { maxWidth: "95vw", width: "95vw" } : {}),
            },
          },
        }}
        title={title}
        noPrimaryAction
        titleBarActionElement={(() => {
          const showComments = isStaff && isSingleManifest;
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              {showComments && (
                <ManifestCommentsPopover processedManifestId={processedManifestIds[0]} />
              )}
              <MultiSelectFilter
                label="Status"
                options={ALL_DISCREPANCY_STATUSES}
                optionLabel={getStatusLabel}
                value={statusFilter}
                onChange={setStatusFilter}
                isValidOption={isDiscrepancyStatus}
                emptyHelperText="Select at least one status to view data"
              />
            </Stack>
          );
        })()}
        contentSx={{ overflow: "hidden" }}
      >
        {/* position: relative wrapper gives children a concrete height to resolve against. */}
        <Box sx={{ flex: 1, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "row",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {/* px matches the section cards' mx so the tiles and cards share a left/right rail. */}
                <Box sx={{ px: 2, pt: 1.5, pb: 1.5, flexShrink: 0 }}>
                  <DiscrepancySummaryBar
                    tiles={summaryTiles}
                    loading={loading}
                    activeKeys={activeTileKeys}
                    onTileClick={handleTileClick}
                  />
                </Box>
                <Box ref={tableScrollRef} sx={{ flex: 1, minWidth: 0, overflow: "auto" }}>
                  {statusFilter.length === 0 ? (
                    <Box sx={{ py: 8, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Select at least one status to view data
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {showEpaCorrectionRows && sectionFilter.includes("epaCorrection") && (
                        <DiscrepancySection
                          ref={epaSectionRef}
                          title="EPA-Requested Corrections"
                          count={filteredEpaCorrectionItems.length}
                          loading={loading}
                          hasUrgent={epaHasUrgent}
                          collapsed={collapsedSections.has("epaCorrection")}
                          onToggleCollapse={() => toggleSectionCollapsed("epaCorrection")}
                          emptyMessage={emptyMessageFor(
                            filteredEpaCorrectionItems.length,
                            epaCorrectionItems.length
                          )}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ px: 2, pt: 1.5, pb: 1.5 }}
                          >
                            EPA-Requested Corrections are open requests from EPA or a state
                            regulator to correct a submitted e-Manifest, each with its own 30-day
                            response deadline.
                          </Typography>
                          <CompliancePane
                            items={filteredEpaCorrectionItems}
                            now={nowRef}
                            loading={loading}
                            activePreviewDocId={activePreviewDocId}
                            creatingExceptionReport={creatingExceptionReport}
                            onViewDocument={openDocumentPreview}
                            onResolveInEManifest={onResolveInEManifest}
                            onAddProofOfContact={onAddProofOfContact}
                            onCreateExceptionReport={onCreateExceptionReport}
                          />
                        </DiscrepancySection>
                      )}

                      {sectionFilter.includes("eManifestMissing") && (
                        <DiscrepancySection
                          ref={missingSectionRef}
                          title="Missing e-Manifests"
                          count={filteredMissingEManifestItems.length}
                          loading={loading}
                          hasUrgent={missingHasUrgent}
                          collapsed={collapsedSections.has("eManifestMissing")}
                          onToggleCollapse={() => toggleSectionCollapsed("eManifestMissing")}
                          emptyMessage={emptyMessageFor(
                            filteredMissingEManifestItems.length,
                            missingEManifestItems.length
                          )}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ px: 2, pt: 1.5, pb: 1.5 }}
                          >
                            Missing e-Manifests are shipments with no matching e-Manifest record
                            once RCRA&apos;s 45- and 60-day compliance thresholds pass.
                          </Typography>
                          <CompliancePane
                            items={filteredMissingEManifestItems}
                            now={nowRef}
                            loading={loading}
                            activePreviewDocId={activePreviewDocId}
                            creatingExceptionReport={creatingExceptionReport}
                            onViewDocument={openDocumentPreview}
                            onResolveInEManifest={onResolveInEManifest}
                            onAddProofOfContact={onAddProofOfContact}
                            onCreateExceptionReport={onCreateExceptionReport}
                          />
                        </DiscrepancySection>
                      )}

                      {sectionFilter.includes("field") && (
                        <DiscrepancySection
                          ref={fieldSectionRef}
                          title="Data Source Conflicts"
                          count={discrepancies.length}
                          loading={loading}
                          collapsed={collapsedSections.has("field")}
                          onToggleCollapse={() => toggleSectionCollapsed("field")}
                          selectedCount={fieldSelectedIds.size}
                          emptyMessage={emptyMessageFor(
                            discrepancies.length,
                            allFieldDiscrepancies.length
                          )}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ px: 2, pt: 1.5, pb: 1.5 }}
                          >
                            Data Source Conflicts surface when a specific value disagrees between
                            vendor data, EPA e-Manifest, and the manifest document — e.g. waste
                            codes, generator info, quantity. Resolve them by updating the vendor
                            record or acknowledging the difference.
                          </Typography>
                          <DiscrepancyBulkActionBar
                            totalSelected={fieldSelectedIds.size}
                            loading={mutationLoading}
                            onOpen={() =>
                              setResolveTarget({
                                rows: selectedFieldDiscrepancies,
                                includeVendorAction: true,
                                clearSelection: clearFieldSelection,
                              })
                            }
                          />
                          {loading ? (
                            <Stack spacing={2} sx={{ p: 1 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell padding="checkbox">
                                      <Skeleton variant="rectangular" width={18} height={18} />
                                    </TableCell>
                                    {[
                                      "Facility",
                                      "Manifest",
                                      "e-Manifest",
                                      "Vendor",
                                      SOURCE_LABEL[WasteManifestSource.ManifestDocument],
                                      "Status",
                                    ].map((h) => (
                                      <TableCell key={h}>
                                        <Skeleton variant="text" width={80} />
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Array.from({ length: 8 }, (_, i) => (
                                    <TableRow key={i}>
                                      <TableCell padding="checkbox">
                                        <Skeleton variant="rectangular" width={18} height={18} />
                                      </TableCell>
                                      <TableCell>
                                        <Skeleton variant="text" width={120} />
                                        <Skeleton variant="text" width={80} />
                                      </TableCell>
                                      <TableCell>
                                        <Skeleton variant="text" width={100} />
                                      </TableCell>
                                      <TableCell>
                                        <Skeleton variant="text" width={140} />
                                      </TableCell>
                                      <TableCell>
                                        <Skeleton variant="text" width={140} />
                                      </TableCell>
                                      <TableCell>
                                        <Skeleton variant="text" width={60} />
                                      </TableCell>
                                      <TableCell>
                                        <Skeleton variant="rounded" width={80} height={24} />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Stack>
                          ) : (
                            // White grid surface so rows don't inherit the card's gray; the grid
                            // is the card's last element, so round its bottom to match the card.
                            <TableContainer
                              sx={{
                                overflow: "visible",
                                bgcolor: "background.paper",
                                borderBottomLeftRadius: "inherit",
                                borderBottomRightRadius: "inherit",
                              }}
                            >
                              <Table
                                size="small"
                                stickyHeader
                                sx={{
                                  "& .MuiTableHead-root .MuiTableCell-head": {
                                    // Stack below the section's sticky title band.
                                    top: STICKY_COLUMN_HEADER_TOP,
                                    // Override MUI's stickyHeader bg (background.default reads near-black in dark mode).
                                    ...stickyOpaquePanelBg,
                                    borderBottom: `1px solid ${theme.palette.background.paper}`,
                                  },
                                  // Top-align so multi-line values line up across the row.
                                  "& .MuiTableBody-root .MuiTableCell-body": {
                                    verticalAlign: "top",
                                  },
                                }}
                              >
                                <TableHead>
                                  <TableRow>
                                    <TableCell padding="checkbox" />

                                    <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>
                                      Facility
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>
                                      Manifest
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                                      e-Manifest
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                                      Vendor
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>
                                      {SOURCE_LABEL[WasteManifestSource.ManifestDocument]}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
                                      Status
                                    </TableCell>
                                    <TableCell padding="checkbox" />
                                  </TableRow>
                                </TableHead>
                                {fieldGroups.map((group) => (
                                  <TableBody key={group.field}>
                                    <SectionHeaderRow
                                      label={group.label}
                                      count={group.items.length}
                                      colSpan={COL_COUNT}
                                      collapsed={collapsedGroups.has(group.field)}
                                      onToggle={() => toggleGroup(group.field)}
                                    />
                                    {!collapsedGroups.has(group.field) &&
                                      group.items.map(renderDiscrepancyRow)}
                                  </TableBody>
                                ))}
                              </Table>
                            </TableContainer>
                          )}
                        </DiscrepancySection>
                      )}

                      {sectionFilter.includes("fieldIssue") && (
                        <DiscrepancySection
                          ref={fieldIssueSectionRef}
                          title="Data Quality Issues"
                          count={fieldIssues.length}
                          loading={loading}
                          collapsed={collapsedSections.has("fieldIssue")}
                          onToggleCollapse={() => toggleSectionCollapsed("fieldIssue")}
                          selectedCount={fieldIssueSelectedIds.size}
                          emptyMessage={emptyMessageFor(
                            fieldIssues.length,
                            allFieldIssueDiscrepancies.length
                          )}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ px: 2, pt: 1.5, pb: 1.5 }}
                          >
                            Data Quality Issues flag values that look wrong on their own — the value
                            itself fails a check, like an invalid Texas waste code format. Resolve
                            or dismiss each issue.
                          </Typography>
                          <DiscrepancyBulkActionBar
                            totalSelected={fieldIssueSelectedIds.size}
                            loading={mutationLoading}
                            onOpen={() =>
                              setResolveTarget({
                                rows: selectedFieldIssueDiscrepancies,
                                includeVendorAction: false,
                                clearSelection: clearFieldIssueSelection,
                              })
                            }
                          />
                          <FieldIssuesPane
                            items={fieldIssues}
                            loading={loading}
                            selectedIds={fieldIssueSelectedIds}
                            onToggleSelect={toggleFieldIssueSelect}
                            onRowMenuOpen={({ anchorEl, discrepancy }) =>
                              setRowMenu({ anchorEl, discrepancy })
                            }
                          />
                        </DiscrepancySection>
                      )}
                    </>
                  )}
                </Box>
              </Box>

              {(previewDoc ?? previewLoading) && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 2,
                        // Match the column header height so the preview top aligns with the first data row.
                        height: STICKY_COLUMN_HEADER_HEIGHT,
                        flexShrink: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: subtleSurfaceBg,
                      }}
                    >
                      <Typography variant="subtitle2">Source Document</Typography>
                      <Stack direction="row" spacing={0.5}>
                        {previewDoc && (
                          <Tooltip title="Open in new tab">
                            <IconButton size="small" onClick={openDocumentInNewTab}>
                              <OpenInNewIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Close preview">
                          <IconButton size="small" onClick={() => setPreviewTarget(null)}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      {previewLoading ? (
                        <Skeleton
                          variant="rectangular"
                          animation="wave"
                          height="100%"
                          width="100%"
                        />
                      ) : previewDoc ? (
                        <FilePreview
                          fileUrl={previewDoc.url}
                          fileExtension={previewDoc.fileExtension}
                        />
                      ) : null}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Dialog>

      <Menu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={rowMenu != null}
        onClose={() => setRowMenu(null)}
      >
        <MenuItem
          disabled={mutationLoading}
          onClick={() => {
            if (rowMenu) {
              const d = rowMenu.discrepancy;
              setResolveTarget({
                rows: [d],
                // Data Quality Issues never offer "Correct Vendor Data" (see
                // RESOLVE_ACTION_DEFS).
                includeVendorAction: d.kind !== DiscrepancyKind.FieldIssue,
                clearSelection: () => {},
              });
              setRowMenu(null);
            }
          }}
        >
          <ListItemIcon>
            <TaskAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Resolve…" secondary="Choose a resolution action for this row" />
        </MenuItem>
        {(() => {
          const isPending = rowMenu?.discrepancy.status === "PENDING";
          return (
            <MenuItem
              disabled={!isPending || mutationLoading}
              onClick={() => {
                if (rowMenu) {
                  void handleReopenPending(rowMenu.discrepancy);
                }
              }}
            >
              <ListItemIcon>
                <UndoIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Mark as Unresolved"
                secondary={
                  isPending
                    ? "Undo “Resolve in e-Manifest” so another action can be taken"
                    : "Only available for pending rows"
                }
              />
            </MenuItem>
          );
        })()}
      </Menu>

      {showProofOfContactForPmId && (
        <ProofOfContactDialog
          open
          onClose={() => setShowProofOfContactForPmId(null)}
          processedManifestId={showProofOfContactForPmId}
        />
      )}

      <ResolveDiscrepanciesDialog
        open={resolveTarget !== null}
        totalCount={resolveTarget?.rows.length ?? 0}
        options={resolveOptions}
        loading={mutationLoading}
        onClose={() => setResolveTarget(null)}
        onConfirm={executeResolveAction}
      />
    </>
  );
}
